/**
 * SessionLockOverlay — Expense App
 *
 * Shown after 15 min of idle activity.
 * User must re-enter password to resume. After 30 min they are logged out.
 */
import React, { useState, useRef, useEffect } from "react";

// Use "/api" in dev (proxied via package.json "proxy") so we never hardcode a port.
const API_BASE = process.env.REACT_APP_API_URL || "/api";

export default function SessionLockOverlay({ user, onUnlock, onLogout }) {
  const [password, setPassword] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  async function handleUnlock(e) {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ email: user.email, password, company: user.company }),
      });
      const data = await res.json();
      if (!res.ok) {
        const locked = data?.locked;
        setError(locked ? "Account is locked. Please log in again." : (data?.error || "Incorrect password"));
        if (locked) setTimeout(onLogout, 2000);
        return;
      }
      setPassword("");
      onUnlock();
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const initials = (user?.name || "?")
    .split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();

  const isEbekmed = user?.company === "ebekmed";
  const logoSrc   = isEbekmed ? "/logo-ebekmed.png" : "/logo-ebek.png";

  return (
    <div style={styles.backdrop}>
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <img src={logoSrc} alt="Ebek" style={{ height: 36, display: "block" }}
               onError={e => { e.target.style.display = "none"; }} />
        </div>

        <div style={styles.lockIcon}>🔒</div>
        <h2 style={styles.title}>Session Locked</h2>
        <p style={styles.subtitle}>
          Your session was locked after 15 minutes of inactivity.
        </p>

        <div style={styles.avatarRow}>
          {user?.profile_photo
            ? <img src={user.profile_photo} alt="" style={{ ...styles.avatar, objectFit: "cover" }} />
            : <div style={styles.avatar}>{initials}</div>}
          <div>
            <div style={styles.userName}>{user?.name}</div>
            <div style={styles.userEmail}>{user?.email}</div>
          </div>
        </div>

        <form onSubmit={handleUnlock} style={styles.form}>
          <div style={{ position: "relative" }}>
            <input
              ref={inputRef}
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(""); }}
              placeholder="Enter your password to continue"
              style={{ ...styles.input, paddingRight: 44 }}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPwd(v => !v)}
              tabIndex={-1}
              style={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                background: "none", border: "none", cursor: "pointer",
                fontSize: 18, lineHeight: 1, color: "#6b7280", padding: 0,
              }}
              aria-label={showPwd ? "Hide password" : "Show password"}
            >
              {showPwd ? "🙈" : "👁"}
            </button>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button
            type="submit"
            disabled={loading || !password}
            style={{ ...styles.btn, opacity: (loading || !password) ? 0.6 : 1 }}
          >
            {loading ? "Verifying…" : "Unlock"}
          </button>
        </form>

        <button onClick={onLogout} style={styles.logoutLink}>
          Not you? Sign in with a different account
        </button>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
    backdropFilter: "blur(6px)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 99999,
  },
  card: {
    background: "#fff", borderRadius: 16, padding: "36px 40px",
    width: "100%", maxWidth: 400, textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
  },
  logoWrap: { marginBottom: 16, display: "flex", justifyContent: "center" },
  lockIcon: { fontSize: 40, marginBottom: 12 },
  title:    { margin: "0 0 8px", fontSize: 22, fontWeight: 700, color: "#111" },
  subtitle: { margin: "0 0 24px", fontSize: 14, color: "#6b7280" },
  avatarRow: {
    display: "flex", alignItems: "center", gap: 12,
    background: "#f9fafb", border: "1px solid #e5e7eb",
    borderRadius: 10, padding: "12px 16px", marginBottom: 24, textAlign: "left",
  },
  avatar: {
    width: 40, height: 40, borderRadius: "50%", background: "#0f766e",
    color: "#fff", display: "flex", alignItems: "center",
    justifyContent: "center", fontWeight: 700, fontSize: 16, flexShrink: 0,
  },
  userName:  { fontWeight: 600, fontSize: 14, color: "#111" },
  userEmail: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  form:  { display: "flex", flexDirection: "column", gap: 12 },
  input: {
    width: "100%", padding: "12px 14px", border: "1px solid #d1d5db",
    borderRadius: 8, fontSize: 14, outline: "none", boxSizing: "border-box",
  },
  error: {
    fontSize: 13, color: "#dc2626", textAlign: "left",
    background: "#fef2f2", border: "1px solid #fecaca",
    borderRadius: 6, padding: "8px 12px",
  },
  btn: {
    padding: "12px", background: "#0f766e", color: "#fff",
    border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer",
  },
  logoutLink: {
    marginTop: 16, background: "none", border: "none",
    color: "#6b7280", fontSize: 13, cursor: "pointer", textDecoration: "underline",
  },
};
